// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { 
  Upload, Eye, FileText, FolderOpen, BookOpen, CheckCircle, 
  AlertCircle, Clock, Play, Pause, RotateCw, X, Info
} from 'lucide-react';
import './IngestionProgress.css';

const IngestionProgress = ({ fileItem, evenements = [], onClose, onRetry }: any) => {
  const [currentStage, setCurrentStage] = useState(0);
  const [stageDetails, setStageDetails] = useState({});
  const [logs, setLogs] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);

  const stages = [
    {
      id: 'RECEIVED',
      name: 'Réception du fichier',
      description: 'Vérification et validation du fichier',
      icon: <Upload size={20} />,
      color: '#8B5CF6'
    },
    {
      id: 'FORMAT_DETECT',
      name: 'Détection du format',
      description: 'Analyse du type de fichier et vérification des doublons',
      icon: <Eye size={20} />,
      color: '#8b5cf6'
    },
    {
      id: 'CONVERT',
      name: 'Conversion',
      description: 'Conversion au format standard si nécessaire',
      icon: <RotateCw size={20} />,
      color: '#f59e0b'
    },
    {
      id: 'OCR',
      name: 'Reconnaissance de texte',
      description: 'Extraction du texte et OCR si nécessaire',
      icon: <FileText size={20} />,
      color: '#10b981'
    },
    {
      id: 'EXTRACT',
      name: 'Extraction du contenu',
      description: 'Extraction complète du texte et métadonnées',
      icon: <FileText size={20} />,
      color: '#06b6d4'
    },
    {
      id: 'CLEAN',
      name: 'Nettoyage du texte',
      description: 'Nettoyage et normalisation du contenu',
      icon: <FolderOpen size={20} />,
      color: '#84cc16'
    },
    {
      id: 'SPLIT',
      name: 'Segmentation',
      description: 'Découpage en segments pour l\'indexation',
      icon: <FolderOpen size={20} />,
      color: '#f97316'
    },
    {
      id: 'EMBEDDINGS',
      name: 'Génération d\'embeddings',
      description: 'Création des vecteurs pour la recherche',
      icon: <BookOpen size={20} />,
      color: '#ec4899'
    },
    {
      id: 'INDEX',
      name: 'Indexation',
      description: 'Insertion dans la base de données vectorielle',
      icon: <BookOpen size={20} />,
      color: '#7C3AED'
    },
    {
      id: 'CATALOG',
      name: 'Catalogage',
      description: 'Ajout au catalogue et métadonnées',
      icon: <BookOpen size={20} />,
      color: '#8b5cf6'
    },
    {
      id: 'DONE',
      name: 'Terminé',
      description: 'Livre prêt pour la recherche et l\'IA',
      icon: <CheckCircle size={20} />,
      color: '#10b981'
    }
  ];

  const getStageIndex = (stageId) => {
    return stages.findIndex(stage => stage.id === stageId);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return '#10b981';
      case 'error': return '#ef4444';
      case 'warning': return '#f59e0b';
      case 'info': return '#8B5CF6';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <CheckCircle size={16} />;
      case 'error': return <AlertCircle size={16} />;
      case 'warning': return <AlertCircle size={16} />;
      case 'info': return <Info size={16} />;
      default: return <Clock size={16} />;
    }
  };

  // Aucune connexion SSE ici : le parent tient déjà l'unique flux du livre et
  // nous passe ses événements. Un second abonnement sur le même book_id
  // ouvrait une connexion HTTP de plus par fichier, pour les mêmes données.
  //
  // L'affichage est entièrement dérivé de la liste reçue : la rejouer en
  // entier à chaque changement rend la reprise après rechargement gratuite.
  useEffect(() => {
    if (!evenements || evenements.length === 0) return;

    let etapeMax = 0;
    const details = {};
    const journal = [];

    for (const ev of evenements) {
      if (!ev || !ev.stage) continue;

      const message = ev.message_fr || ev.stage;

      if (ev.stage === 'ERROR') {
        journal.push({ message: `Échec de l'ingestion : ${message}`, status: 'error' });
        continue;
      }

      const index = getStageIndex(ev.stage);
      if (index === -1) {
        // Étape hors stepper (ex. COVER_EXTRACTION) : journalisée sans plus.
        journal.push({ message, status: ev.status === 'error' ? 'error' : 'info' });
        continue;
      }

      etapeMax = Math.max(etapeMax, index);

      const statutEtape =
        ev.status === 'success' ? 'success'
        : ev.status === 'error' ? 'error'
        : ev.status === 'warning' ? 'warning'
        : 'running';

      details[ev.stage] = {
        status: statutEtape,
        progress: typeof ev.percent === 'number' ? ev.percent : 0,
        message
      };
      journal.push({ message, status: statutEtape === 'running' ? 'info' : statutEtape });
    }

    setCurrentStage(etapeMax);
    setStageDetails(details);
    setLogs(journal.map((ligne, i) => ({
      id: i,
      message: ligne.message,
      status: ligne.status,
      timestamp: new Date()
    })));
  }, [evenements]);

  return (
    <div className="ingestion-progress">
      {/* En-tête */}
      <div className="progress-header">
        <div className="file-info">
          <h3>{fileItem.name}</h3>
          <p className="file-size">{fileItem.fileSize}</p>
        </div>
        <div className="progress-actions">
          <button 
            className="expand-btn"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label={isExpanded ? 'Réduire' : 'Développer'}
          >
            {isExpanded ? <X size={16} /> : <Info size={16} />}
          </button>
          {onClose && (
            <button 
              className="close-btn"
              onClick={onClose}
              aria-label="Fermer"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Barre de progression globale */}
      <div className="global-progress">
        <div className="progress-bar">
          <div 
            className="progress-fill"
            style={{ width: `${fileItem.progress}%` }}
          />
        </div>
        <div className="progress-text">
          {fileItem.progress}% - {fileItem.message}
        </div>
      </div>

      {/* Étapes détaillées */}
      <div className="stages-container">
        {stages.map((stage, index) => {
          const isActive = index === currentStage;
          const isCompleted = index < currentStage;
          const isPending = index > currentStage;
          
          return (
            <div 
              key={stage.id}
              className={`stage-item ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isPending ? 'pending' : ''}`}
            >
              <div className="stage-icon">
                {isCompleted ? (
                  <CheckCircle size={20} color="#10b981" />
                ) : (
                  <div 
                    className="stage-icon-bg"
                    style={{ 
                      backgroundColor: isActive ? stage.color : '#e5e7eb',
                      color: isActive ? 'white' : '#6b7280'
                    }}
                  >
                    {stage.icon}
                  </div>
                )}
              </div>
              
              <div className="stage-content">
                <h4 className="stage-name">{stage.name}</h4>
                <p className="stage-description">{stage.description}</p>
                
                {isActive && stageDetails[stage.id] && (
                  <div className="stage-details">
                    <p className="stage-message">{stageDetails[stage.id].message}</p>
                    {stageDetails[stage.id].progress && (
                      <div className="stage-progress">
                        <div 
                          className="stage-progress-fill"
                          style={{ width: `${stageDetails[stage.id].progress}%` }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {isActive && (
                <div className="stage-status">
                  <RotateCw size={16} className="spinning" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Logs détaillés (si développé) */}
      {isExpanded && (
        <div className="logs-container">
          <h4>Logs détaillés</h4>
          <div className="logs-list">
            {logs.map(log => (
              <div key={log.id} className={`log-item log-${log.status}`}>
                <div className="log-icon">
                  {getStatusIcon(log.status)}
                </div>
                <div className="log-content">
                  <p className="log-message">{log.message}</p>
                  <span className="log-time">
                    {log.timestamp.toLocaleTimeString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="progress-actions">
        {fileItem.status === 'error' && onRetry && (
          <button className="retry-btn" onClick={onRetry}>
            <RotateCw size={16} />
            Réessayer
          </button>
        )}
        
        {fileItem.status === 'completed' && (
          <button className="view-btn">
            <Eye size={16} />
            Voir dans la bibliothèque
          </button>
        )}
      </div>
    </div>
  );
};

export default IngestionProgress;
